import React, { useRef, useState } from "react";
import "./BulkImportModal.css";
import { Box } from "@mui/material";
import Title from "../title/Title";
import { PrimaryButton, SecondaryButton } from "../buttonComponent/ButtonComponent";
import { ToggleSwitch } from "../toggle/ToggleSwitch";
import ModalCloseButton from "./ModalCloseButton";
import { generateCSVTemplate } from "../../config/utilities/csvUtils";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "700px",
  minWidth: "320px",
  bgcolor: "#FFFFFF",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  p: 0,
  boxShadow:
    "0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)",
  borderRadius: "12px",
  maxHeight: "90vh",
  overflow: "hidden",
};

const BulkImportModal = ({
  isOpen,
  onClose,
  onImport,
  loading = false,
}) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [sendEmail, setSendEmail] = useState(true);

  if (!isOpen) return null;

  const handleDownloadTemplate = () => {
    const csvContent = generateCSVTemplate();
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "family_import_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith(".csv")) {
      setValidationErrors(["Please select a CSV file (.csv)"]);
      setSelectedFile(null);
      return;
    }

    // Validate file size (max 5MB)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_FILE_SIZE) {
      setValidationErrors([`File size exceeds maximum limit of 5MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`]);
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setValidationErrors([]);
  };

  const handleImport = () => {
    if (!selectedFile) {
      setValidationErrors(["Please select a CSV file"]);
      return;
    }

    // Pass the file and sendEmail flag to the import handler
    onImport(selectedFile, sendEmail);
  };

  const handleClose = () => {
    setSelectedFile(null);
    setValidationErrors([]);
    setSendEmail(true); // Reset to default
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  return (
    <div className="modal-backdrop">
      <Box sx={style}>
        <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div className="modal-header-content" style={{ flexShrink: 0 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                alignItems: "flex-start",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1, minWidth: 0, paddingRight: "0.5rem" }}>
                <Title
                  title="Bulk Import Families"
                  subtitle="Import multiple families from a CSV file"
                  titleSize="1.125rem"
                  subtitleSize="0.875rem"
                  gap="0.25rem"
                  lineHeight="1.75rem"
                />
              </div>
              <ModalCloseButton onClick={handleClose} />
            </div>
          </div>

          <div className="bulk-import-content">
            <div className="bulk-import-instructions">
            <div className="bulk-import-email-toggle">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", flex: 1 }}>
                  <label style={{ fontSize: "0.875rem", fontWeight: 600, color: "#101828", margin: 0 }}>
                    Send Onboarding Email
                  </label>
                  <span style={{ fontSize: "0.75rem", color: "#475467", lineHeight: "1.25rem" }}>
                    Send onboarding email and temporary password to parents when families are imported
                  </span>
                </div>
                <div style={{ marginLeft: "1rem" }}>
                  <ToggleSwitch
                    checked={sendEmail}
                    onChange={setSendEmail}
                    disabled={loading}
                    ariaLabel="Toggle onboarding email"
                  />
                </div>
              </div>
            </div>
              <h3 style={{ margin: "0 0 0.75rem 0", fontSize: "0.875rem", fontWeight: 600, color: "#101828" }}>
                CSV Format Requirements
              </h3>
              <div className="bulk-import-notes">
                <p style={{ margin: "0 0 0.5rem 0", fontSize: "0.75rem", color: "#475467", lineHeight: "1.25rem" }}>
                  <strong>Important Notes:</strong>
                </p>
                <ul style={{ margin: "0 0 1rem 0", paddingLeft: "1.25rem", fontSize: "0.75rem", color: "#475467", lineHeight: "1.5rem" }}>
                  <li><strong>One Row Per Family:</strong> Each row represents one complete family with all parents and children.</li>
                  <li><strong>Primary Parent:</strong> EMAIL, FIRST_NAME, LAST_NAME, CALLING_CODE, and CONTACT fields are required for the primary parent. CALLING_CODE should be the country code (e.g., "1" for USA, "44" for UK) without the plus sign.</li>
                  <li><strong>Alternate Parent:</strong> ALTERNATE_EMAIL, ALTERNATE_FIRST_NAME, ALTERNATE_LAST_NAME, ALTERNATE_CALLING_CODE, and ALTERNATE_CONTACT fields are optional for a second parent. ALTERNATE_CALLING_CODE should be the country code without the plus sign.</li>
                  <li><strong>Address Components:</strong> ADDRESS_ONE, ADDRESS_TWO, CITY, STATE, and POSTCODE will be combined to form the complete address.</li>
                  <li><strong>Multiple Children:</strong> Up to 4 children can be included per family using Child 1, Child 2, Child 3, and Child 4 columns.</li>
                  <li><strong>Child Fields:</strong> For each child, provide First Name, Last Name, Preferred Name, Date of Birth, Class, and preference columns for each day (MON, TUE, WED, THU, FRI).</li>
                  <li><strong>Child Preferences:</strong> Each child has preference columns for Monday through Friday (Child X Preferences: MON, TUE, WED, THU, FRI). These columns come after the Class column. Enter service preferences like 'AM', 'PM', 'ECA', 'AM,PM', 'AM,ECA' or etc.. for each day. Multiple services should be comma-separated. </li>
                  <li><strong>Empty Children:</strong> Leave child fields empty if a family has fewer than 4 children.</li>
                </ul>
                <p style={{ margin: "0", fontSize: "0.75rem", color: "#475467", lineHeight: "1.25rem" }}>
                  <strong>Required Fields:</strong> EMAIL, FIRST_NAME, LAST_NAME, and at least one child (Child 1 First Name and Child 1 Last Name)
                  <br/>
                  <br/>
                </p>
              </div>
            </div>

            <div className="bulk-import-actions">
              <SecondaryButton
                label="Download Template"
                width="auto"
                imageIcon={false}
                onClickFunction={handleDownloadTemplate}
                disable={loading}
              />
              <SecondaryButton
                label={selectedFile ? (selectedFile.name.length > 30 ? selectedFile.name.substring(0, 30) + "..." : selectedFile.name) : "Add File"}
                width="auto"
                imageIcon={false}
                onClickFunction={handleFileSelect}
                disable={loading}
              />
            </div>

            {validationErrors.length > 0 && (
              <div className="bulk-import-errors">
                <p style={{ margin: "0 0 0.5rem 0", fontSize: "0.75rem", fontWeight: 600, color: "#D92D20" }}>
                  Validation Errors:
                </p>
                <ul style={{ margin: 0, paddingLeft: "1.25rem", fontSize: "0.75rem", color: "#D92D20" }}>
                  {validationErrors.map((error, index) => (
                    <li key={index} style={{ marginBottom: "0.25rem" }}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {selectedFile && validationErrors.length === 0 && (
              <div className="bulk-import-success">
                <p style={{ margin: 0, fontSize: "0.75rem", color: "#027A48" }}>
                  ✓ CSV file selected. Ready to import.
                </p>
              </div>
            )}
          </div>

          <div className="button-container-delete" style={{ flexShrink: 0 }}>
            <SecondaryButton
              label="Cancel"
              width="170px"
              imageIcon={false}
              onClickFunction={handleClose}
              disable={loading}
            />
            <PrimaryButton
              disable={!selectedFile || validationErrors.length > 0 || loading}
              width="170px"
              label={loading ? "Importing..." : "Import"}
              onClickFunction={handleImport}
              loading={loading}
            />
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </Box>
    </div>
  );
};

export default BulkImportModal;
